import React from 'react';
import { Link } from 'react-router-dom';
import appScreenshots from '../assets/centmap_app_screenshots_multiple.jpeg';
import { Footer } from '../components/Footer';
import { JsonLd } from '../components/JsonLd';
import { Seo } from '../components/Seo';
import { APP_STORE_URL, PLAY_STORE_URL } from '../lib/appLinks';
import { CITIES, PAGE_SEO } from '../lib/seo';
import { breadcrumbStructuredData, webPageStructuredData } from '../lib/structuredData';

const INLINE_LINK = 'text-emerald-600 hover:text-emerald-700 underline';

const SCHEMA = [
  webPageStructuredData(PAGE_SEO.app),
  breadcrumbStructuredData([{ name: 'Get the app', path: PAGE_SEO.app.path }]),
];

export const AppPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <Seo {...PAGE_SEO.app} />
      <JsonLd id="centmap-app-schema" data={SCHEMA} />
      <div className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Get the App!</h1>
        <p className="text-gray-600 mb-8 leading-relaxed">
          Take Centmap with you wherever you go. Download our mobile app to discover
          the best deals at bars and restaurants near you.
        </p>

        {/* App screenshots */}
        <div className="mb-10">
          <img
            src={appScreenshots}
            alt="Centmap app screenshots"
            className="w-full rounded-xl shadow-lg"
          />
        </div>

        {/* Download buttons */}
        <div className="flex flex-col gap-3 mb-10 w-full sm:flex-row">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-3 px-4 rounded-lg bg-gray-900 text-white font-medium text-center hover:bg-gray-800 transition-colors"
          >
            Download on the App Store
          </a>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-3 px-4 rounded-lg border border-gray-300 text-gray-900 font-medium text-center hover:bg-gray-100 transition-colors"
          >
            Get it on Google Play
          </a>
        </div>

        {/* Features */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">Why use the app?</h2>
          <ul className="space-y-4 text-gray-600">
            <li className="flex items-start gap-3">
              <svg className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Get notified about new deals near you</span>
            </li>
            <li className="flex items-start gap-3">
              <svg className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Save your favorite spots for quick access</span>
            </li>
            <li className="flex items-start gap-3">
              <svg className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Works offline - browse deals without internet</span>
            </li>
          </ul>
        </div>

        {/* Where to go next. These are the contextual links in the internal
            linking blueprint (SEO.md): the download page is a common landing
            page from the app stores and from social, and on its own it is a
            dead end. */}
        <div className="mt-10 space-y-3 border-t border-gray-100 pt-8 text-sm text-gray-500">
          <p>
            Prefer the web?{' '}
            <Link to="/" className={INLINE_LINK}>
              Continue to the map
            </Link>
            .
          </p>
          <p>
            Want to know where the prices come from, or how to add one?{' '}
            <Link to="/faq" className={INLINE_LINK}>
              Read the FAQ
            </Link>{' '}
            or{' '}
            <Link to="/support" className={INLINE_LINK}>
              send us feedback
            </Link>
            .
          </p>
          <p>
            Browse beer prices in{' '}
            {CITIES.map((city, index) => (
              <React.Fragment key={city.slug}>
                {index > 0 && (index === CITIES.length - 1 ? ' and ' : ', ')}
                <Link to={`/${city.slug}`} className={INLINE_LINK}>
                  {city.name}
                </Link>
              </React.Fragment>
            ))}
            .
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};
